import * as React from 'react';

import type { Filter, HeaderLevel } from '@coursera-web/cds-labs';
import { CollectionsModule, getModuleLayoutConfig } from '@coursera-web/cds-labs';

import type { ButtonProps } from '@coursera/cds-core';
import type { ModuleType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { CampaignCollectionsModuleFragment } from 'bundles/campaigns/__generated__/contentfulTypes';
import { useCampaignModulesData } from 'bundles/campaigns/context/CampaignModulesDataContext';
import useCollection from 'bundles/campaigns/hooks/useCollection';

type Props = {
  headlineComponent?: HeaderLevel;
  isPageGridContainerAppliedOnPage?: boolean;
};

export const useShouldShowCampaignCollectionsModule = () => {
  const { campaignData, loading } = useCampaignModulesData();
  return !!campaignData?.collectionsModule?.contentDataJson || loading;
};

const CampaignCollectionsModuleRenderer = ({ headlineComponent, isPageGridContainerAppliedOnPage }: Props) => {
  const track = useTracker();
  const [currentCollectionSetIndex, setCurrentCollectionSetIndex] = React.useState(0);

  const moduleData = useCampaignModulesData()?.campaignData?.collectionsModule;
  const content = moduleData?.contentDataJson;

  const {
    description,
    ctaText,
    ctaTarget,
    ctaVariant,
    headline,
    showAllEntries,
    numOfEntriesPerCollection,
    collectionSetsCollection,
  } = content || {};

  const moduleEventingData = {
    moduleType: 'collections_module' as ModuleType,
    heading: headline ?? '',
    body: description ?? '',
    campaign: {
      id: moduleData?.campaignId ?? '',
      matchedCountryCode: moduleData?.matchedCountryCode ?? '',
      epicExperimentId: moduleData?.epicExperimentId ?? '',
      regionName: moduleData?.regionName ?? '',
      campaignName: moduleData?.campaignName ?? '',
    },
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_merchandising_module',
    {
      merchandisingModule: moduleEventingData,
    }
  );

  const trackClick = () => {
    track('click_merchandising_module', {
      merchandisingModule: moduleEventingData,
      button: {
        name: 'primary_cta',
        linkURL: ctaTarget ?? '',
        linkName: ctaText ?? '',
        linkType: 'internal',
      },
    });
  };

  const collectionSets =
    (collectionSetsCollection as CampaignCollectionsModuleFragment['collectionSetsCollection'])?.items ?? [];
  const currentCollectionSet = collectionSets[currentCollectionSetIndex];
  const { loading: isCollectionLoading, collection } = useCollection({
    queryCollectionId: currentCollectionSet?.collectionModuleId ?? '',
    additionalInput: {
      preset_collection_id: currentCollectionSet?.presetCollectionId ?? '',
    },
  });

  if (!useShouldShowCampaignCollectionsModule() || !content) {
    return null;
  }

  const filters: Filter[] = [];
  collectionSets.forEach((collectionSet) => {
    if (collectionSet?.filterName) {
      filters.push({
        id: collectionSet.sys.id,
        filterName: collectionSet.filterName,
        filterImage: collectionSet.filterImage,
      });
    }
  });

  const filterTrackingName = (filterName: string) => {
    return `collections_filter_chip_${filterName
      .toLocaleLowerCase()
      .split(/\s&\s|\s+/)
      .join('_')
      .replace(/-/, '_')}`;
  };

  const handleFilterChange = (index: number, filterName: string) => {
    track('click_merchandising_module', {
      merchandisingModule: moduleEventingData,
      button: {
        name: 'collections_filter_chip',
        linkName: filterTrackingName(filterName),
      },
    });

    setCurrentCollectionSetIndex(index);
  };

  return (
    <div ref={visibilityTrackingRef}>
      <CollectionsModule
        {...getModuleLayoutConfig(content)}
        {...{
          filters,
          currentCollectionSetIndex,
          collections: collection ? [collection] : undefined,
          isCollectionsLoading: isCollectionLoading,
          onFilterChange: handleFilterChange,
          isPageGridContainerAppliedOnPage,
          showAllEntries,
          description,
          headline,
          headlineComponent,
          numOfEntriesPerCollection: numOfEntriesPerCollection ?? undefined,
          cta: {
            target: ctaTarget,
            variant: ctaVariant as ButtonProps['variant'],
            text: ctaText,
            onClick: trackClick,
          },
        }}
      />
    </div>
  );
};

export default CampaignCollectionsModuleRenderer;
